// src/pages/Clients.js
// Fiches clients d'une agence (plan Enterprise) : liste, création, et détail d'un client avec ses audits.
import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Briefcase, Plus, Loader2, Lock, ArrowLeft, Trash2, Save, Search } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';

const API_URL = 'https://cyberguard-api-ka8f.onrender.com';
const input = 'w-full bg-black border border-zinc-800 p-3 text-white font-mono text-sm focus:border-purple-500 outline-none placeholder:text-zinc-700';
const GRADE_COLORS = { A: '#22c55e', B: '#84cc16', C: '#eab308', D: '#f97316', E: '#ef4444', F: '#dc2626', G: '#991b1b' };
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const EMPTY = { name: '', contact_name: '', contact_email: '', notes: '' };

function Grade({ audit }) {
  if (!audit) return <span className="text-zinc-600 font-mono text-xs">—</span>;
  const c = GRADE_COLORS[audit.dpe_grade] || '#71717a';
  return (
    <span className="inline-flex items-center gap-2 font-mono text-xs">
      <span className="w-6 h-6 inline-flex items-center justify-center font-black text-white" style={{ background: c }}>{audit.dpe_grade}</span>
      <span className="text-zinc-300">{audit.security_score}/100</span>
    </span>
  );
}

function Locked() {
  const navigate = useNavigate();
  return (
    <div className="bg-zinc-900 border border-zinc-800 p-6 flex flex-col md:flex-row items-center justify-between gap-4">
      <div className="flex items-center gap-4">
        <Lock className="w-6 h-6 text-zinc-500 shrink-0" />
        <div>
          <p className="text-white font-mono font-bold text-sm">Fiches clients — plan Enterprise</p>
          <p className="text-zinc-500 text-xs mt-1">Classez vos audits par client et suivez l'évolution de chacun.</p>
        </div>
      </div>
      <button onClick={() => navigate('/billing')} className="bg-purple-600 hover:bg-purple-700 px-5 py-2 font-mono text-xs uppercase text-white shrink-0">Voir les plans →</button>
    </div>
  );
}

function ClientForm({ initial, onSubmit, submitLabel, busy }) {
  const [form, setForm] = useState(initial);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(form, () => setForm(EMPTY)); }} className="grid gap-3 sm:grid-cols-2">
      <input value={form.name} onChange={set('name')} placeholder="Nom du client *" maxLength={120} className={input} aria-label="Nom du client" required />
      <input value={form.contact_name} onChange={set('contact_name')} placeholder="Contact" maxLength={120} className={input} aria-label="Contact" />
      <input type="email" value={form.contact_email} onChange={set('contact_email')} placeholder="Email du contact" className={input} aria-label="Email du contact" />
      <textarea value={form.notes} onChange={set('notes')} placeholder="Notes internes (jamais montrées au client)" rows={1} maxLength={2000} className={`${input} sm:col-span-1 resize-y`} aria-label="Notes" />
      <div className="sm:col-span-2">
        <button type="submit" disabled={busy || !form.name.trim()} className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 px-5 py-3 font-mono text-xs uppercase tracking-widest text-white">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : submitLabel === 'Ajouter' ? <Plus className="w-3 h-3" /> : <Save className="w-3 h-3" />} {submitLabel}
        </button>
      </div>
    </form>
  );
}

export function ClientList() {
  const { token, user } = useAuth();
  const allowed = user?.subscription_plan === 'enterprise';
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    if (!allowed) { setLoading(false); return; }
    try {
      const r = await axios.get(`${API_URL}/api/clients`, { headers: { Authorization: `Bearer ${token}` } });
      setClients(r.data || []);
    } catch {
      toast.error('Impossible de charger vos clients');
    } finally {
      setLoading(false);
    }
  }, [allowed, token]);
  useEffect(() => { load(); }, [load]);

  const create = async (form, reset) => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/clients`, form, { headers: { Authorization: `Bearer ${token}` } });
      reset();
      toast.success(`${form.name} ajouté`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const shown = clients.filter((c) => `${c.name} ${c.contact_name} ${c.contact_email}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <DashboardLayout>
      <div className="max-w-5xl space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-white font-mono uppercase tracking-tighter">Clients</h1>
          <p className="text-zinc-500 text-sm mt-2 max-w-2xl">Rattachez vos audits à vos clients pour suivre chacun d'eux et leur envoyer des rapports à votre marque.</p>
        </div>
        {!allowed ? <Locked /> : (
          <>
            <div className="bg-zinc-900 border border-zinc-800 p-6 space-y-4">
              <p className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest">Nouveau client</p>
              <ClientForm initial={EMPTY} onSubmit={create} submitLabel="Ajouter" busy={busy} />
            </div>
            {loading ? <p className="text-zinc-500 font-mono text-xs">Chargement…</p> : clients.length === 0 ? (
              <p className="text-zinc-500 text-sm">Aucun client pour l'instant.</p>
            ) : (
              <div className="space-y-3">
                {clients.length > 6 && (
                  <div className="relative max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un client" className={`${input} pl-10`} aria-label="Rechercher" />
                  </div>
                )}
                <div className="bg-zinc-900 border border-zinc-800 overflow-x-auto">
                  <table className="w-full text-left text-sm" data-testid="clients-table">
                    <thead className="text-zinc-500 font-mono text-[10px] uppercase tracking-widest border-b border-zinc-800">
                      <tr><th className="p-4">Client</th><th className="p-4">Dernier audit</th><th className="p-4 hidden sm:table-cell">Audits</th><th className="p-4 hidden md:table-cell">Date</th></tr>
                    </thead>
                    <tbody>
                      {shown.map((c) => (
                        <tr key={c.client_id} className="border-b border-zinc-800/60 hover:bg-zinc-800/30">
                          <td className="p-4">
                            <Link to={`/clients/${c.client_id}`} className="text-white font-semibold hover:text-purple-300">{c.name}</Link>
                            {c.last_audit && <div className="text-zinc-500 font-mono text-xs">{c.last_audit.target_domain}</div>}
                          </td>
                          <td className="p-4"><Grade audit={c.last_audit} /></td>
                          <td className="p-4 hidden sm:table-cell text-zinc-400 font-mono text-xs">{c.audit_count}</td>
                          <td className="p-4 hidden md:table-cell text-zinc-500 font-mono text-xs">{fmt(c.last_audit?.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

export function ClientDetail() {
  const { id } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const allowed = user?.subscription_plan === 'enterprise';
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!allowed) { setLoading(false); return; }
    axios.get(`${API_URL}/api/clients/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setClient(r.data))
      .catch(() => setClient(null))
      .finally(() => setLoading(false));
  }, [allowed, id, token]);

  const save = async (form) => {
    setBusy(true);
    try {
      const r = await axios.put(`${API_URL}/api/clients/${id}`, form, { headers: { Authorization: `Bearer ${token}` } });
      setClient((c) => ({ ...c, ...r.data }));
      toast.success('Fiche mise à jour');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Supprimer la fiche ${client.name} ? Ses audits restent dans votre compte.`)) return;
    await axios.delete(`${API_URL}/api/clients/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    toast.success('Fiche supprimée');
    navigate('/clients');
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl space-y-6">
        <Link to="/clients" className="inline-flex items-center gap-2 text-zinc-500 hover:text-white font-mono text-xs uppercase"><ArrowLeft className="w-4 h-4" /> Clients</Link>
        {!allowed ? <Locked /> : loading ? <p className="text-zinc-500 font-mono text-xs">Chargement…</p> : !client ? (
          <p className="text-zinc-400">Client introuvable.</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <h1 className="text-2xl font-bold text-white font-mono tracking-tight flex items-center gap-3"><Briefcase className="w-6 h-6 text-purple-400" /> {client.name}</h1>
              <Link to={`/audits/new?client=${client.client_id}`} className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 px-5 py-3 font-mono text-xs uppercase tracking-widest text-white">
                <Plus className="w-3 h-3" /> Nouvel audit pour ce client
              </Link>
            </div>

            <div className="bg-zinc-900 border border-zinc-800">
              <div className="px-6 py-4 border-b border-zinc-800 text-white font-mono font-bold text-xs uppercase tracking-widest">Audits ({client.audits.length})</div>
              {client.audits.length === 0 ? (
                <p className="p-6 text-zinc-500 text-sm">Aucun audit rattaché. Lancez-en un, ou rattachez un audit existant depuis sa page.</p>
              ) : (
                <ul className="divide-y divide-zinc-800/60">
                  {client.audits.map((a) => (
                    <li key={a.audit_id}>
                      <Link to={`/audits/${a.audit_id}`} className="flex items-center justify-between gap-4 px-6 py-3 hover:bg-zinc-800/30">
                        <span className="min-w-0">
                          <span className="block text-white font-mono text-sm truncate">{a.target_domain}</span>
                          <span className="text-zinc-500 font-mono text-xs">{fmt(a.created_at)}{a.source === 'scheduled' ? ' · surveillance' : ''}</span>
                        </span>
                        <Grade audit={a} />
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="bg-zinc-900 border border-zinc-800 p-6 space-y-4">
              <p className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest">Fiche</p>
              <ClientForm key={client.client_id} initial={{ name: client.name, contact_name: client.contact_name, contact_email: client.contact_email, notes: client.notes }} onSubmit={save} submitLabel="Enregistrer" busy={busy} />
              <button onClick={remove} className="inline-flex items-center gap-2 text-red-400 hover:text-red-300 text-xs font-mono uppercase"><Trash2 className="w-3 h-3" /> Supprimer la fiche</button>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
